import { useCallback, useEffect, useRef, useState } from 'react';

import type { AssetUploadMetadata, CreateBatchRequest } from '../../api';
import { runBatchUpload, runSidecarUpload, type UploadItem } from './batchUpload';
import type { DetectedCarousel } from './carouselDetect';
import { runCarouselUpload, type CarouselUploadItem } from './carouselUpload';
import type { AcceptedFile } from './constants';
import type { DetectedAd } from './olaDetect';
import type { SidecarFile } from './pairSidecars';
import { runOlaUpload, type OlaUploadItem } from './olaUpload';

/** What the hook adds to a runner's options: the abort signal and the state sink. */
interface RunContext<TItem> {
  label?: string | null;
  metadata?: AssetUploadMetadata | null;
  signal: AbortSignal;
  onChange: (items: TItem[]) => void;
}

export interface UploadRun<TInput, TItem, TResult> {
  items: TItem[];
  isUploading: boolean;
  start: (
    input: TInput,
    label?: string | null,
    metadata?: AssetUploadMetadata | null,
  ) => Promise<TResult>;
  cancel: () => void;
}

/**
 * React wrapper over an upload orchestrator.
 *
 * Holds the item list, the in-flight flag and the AbortController. All the
 * decisions live in the runner; this only turns its `onChange` callback into
 * state and makes sure an unmount cancels rather than leaving uploads running
 * against a modal that no longer exists.
 */
export function useUploadRun<TInput, TItem, TResult>(
  run: (input: TInput, context: RunContext<TItem>) => Promise<TResult>,
): UploadRun<TInput, TItem, TResult> {
  const [items, setItems] = useState<TItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const start = useCallback(
    async (input: TInput, label?: string | null, metadata?: AssetUploadMetadata | null) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setIsUploading(true);

      try {
        return await run(input, {
          label,
          metadata,
          signal: controller.signal,
          onChange: setItems,
        });
      } finally {
        // In `finally` so a throw out of the orchestrator can't strand the modal
        // showing a Cancel button for uploads that already stopped. Guarded on
        // identity because a second `start` would otherwise have its live
        // controller cleared by the first run's teardown, leaving `cancel` and
        // the unmount abort pointing at nothing.
        if (abortRef.current === controller) {
          setIsUploading(false);
          abortRef.current = null;
        }
      }
    },
    [run],
  );

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  return { items, isUploading, start, cancel };
}

/**
 * The file route's input carries the kind every file becomes, so `start`
 * cannot be called without one. No default.
 */
export interface BatchInput {
  accepted: AcceptedFile[];
  assetType: CreateBatchRequest['asset_type'];
}

const runBatch = ({ accepted, assetType }: BatchInput, context: RunContext<UploadItem>) =>
  runBatchUpload({ accepted, assetType, ...context });

/** The OLA route has one kind, so its input is just the ads. */
const runOla = (ads: DetectedAd[], context: RunContext<OlaUploadItem>) =>
  runOlaUpload({ ads, ...context });

/** The carousel route: one asset per carousel, its frames' sources and copy on the input. */
const runCarousels = (carousels: DetectedCarousel[], context: RunContext<CarouselUploadItem>) =>
  runCarouselUpload({ carousels, ...context });

/** Upload with JSON: each image's metadata travels as its own JSON, so none is set here. */
const runSidecar = (accepted: SidecarFile[], { label, signal, onChange }: RunContext<UploadItem>) =>
  runSidecarUpload({ accepted, label, signal, onChange });

export const useBatchUpload = () => useUploadRun(runBatch);
export const useSidecarUpload = () => useUploadRun(runSidecar);
export const useOlaUpload = () => useUploadRun(runOla);
export const useCarouselUpload = () => useUploadRun(runCarousels);
